// ── Notes panel — reusable notes list/add for any entity ──
import { useState, useEffect, useCallback } from "react";
import { B } from "../../../shared/theme";
import { fmtDate } from "../../../shared/format";
import { Badge, Btn, ErrorMsg, Icon } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const NotesPanel = ({ entitytype, entityid, accountid, loanid, scope = "record", source, title = "Notes", onChange, placeholder = "Add a note…", compact = false }) => {
    const { api, currentUser, role, setModal, setConfirm, delRec } = useApp();
    const [notes, setNotes] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const qs = scope === "account" ? `accountid=${accountid}`
            : scope === "loan"    ? `loanid=${loanid}`
            : `entitytype=${entitytype}&entityid=${entityid}`;

    const load = useCallback(() => {
      api.get(`getNotes?${qs}`).then(setNotes).catch(() => setNotes([]));
    }, [qs]);
    useEffect(() => { load(); }, [load]);
    // Reload this list AND tell the parent (e.g. Bank Activity's notes count).
    const refresh = () => { load(); if (onChange) onChange(); };

    const add = async () => {
      if (!text.trim()) return;
      setBusy(true); setErr(null);
      try {
        await api.post("saveNote", {
          entitytype, entityid, accountid,
          loanid: loanid || null,
          source: source || entitytype,
          note: text.trim(),
          notedate: new Date().toISOString().slice(0, 10),
          createdby: currentUser?.email || null
        });
        setText(""); refresh();
      } catch (e) { setErr(e.message || "Save failed"); }
      setBusy(false);
    };

return (
    <div style={{ marginTop: 4 }}>
      {!compact && <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: "#116588", borderBottom: "1px solid #e3e8ec", paddingBottom: 6, marginBottom: 12 }}>{title}</div>}
        {err && <ErrorMsg msg={err} />}
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
            placeholder={placeholder}
            style={{ flex: 1, padding: "8px 10px", border: "1px solid #ddd", borderRadius: 4, fontSize: 13, resize: "vertical", fontFamily: "inherit" }} />
          <Btn onClick={add} disabled={busy || !text.trim()}>{busy ? "…" : "Add"}</Btn>
        </div>
        {notes.length === 0 && <div style={{ color: B.black40, fontSize: 13 }}>No notes yet.</div>}
        {notes.map(n => (
          <div key={n.id} style={{ borderLeft: `3px solid ${B.cyan}`, padding: "6px 10px", marginBottom: 8, background: "#f8fbfc", borderRadius: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
              <Badge>{n.source || n.entitytype}</Badge>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: B.black40 }}>{fmtDate(n.createdat)}{n.createdby ? ` · ${n.createdby}` : ""}</span>
                {(role?.canEdit || role?.isAdmin) && (
                  <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                    {role?.canEdit && <Btn title="Edit" onClick={() => setModal({ type: "note-edit", rec: n, onDone: refresh })}><Icon type="edit" /></Btn>}
                    {role?.isAdmin && <Btn title="Delete" variant="danger" onClick={() => setConfirm({ msg: "Delete this note?", onConfirm: () => delRec("Notes", n.id, "Deleted note", refresh) })}><Icon type="delete" /></Btn>}
                  </div>
                )}
              </div>
            </div>
            {n.context && <div style={{ fontSize: 11, color: B.cyanDark, marginBottom: 2 }}>{n.context}</div>}
            <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{n.note}</div>
          </div>
        ))}
      </div>
    );
  };
